import { Flex, Box, useColorMode } from '@chakra-ui/react';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { TexasHeader } from '@texas/components/shared/TexasHeader';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { CustomerProductApprovalModal } from './CustomerProductApprovalModal';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { customerProductApprovalColumns } from './Columns';
import { ProductApproval } from '@texas/api/endpoints/productApprovalsApi';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { RowSelectionState } from '@tanstack/react-table';
import { ProductApprovalMenu } from '@texas/components/shared/productApproval/ProductApprovalMenu';
import { CustomerProductApprovalFilters } from './CustomerProductApprovalFilters';
import {
  CustomerProductApprovalsProvider,
  useCustomerProductApprovals,
  defaultPageSize,
} from './CustomerProductApprovalsContext';

interface CustomerProductApprovalsProps {
  customerId: number;
}

export function CustomerProductApprovals({
  customerId,
}: CustomerProductApprovalsProps) {
  return (
    <CustomerProductApprovalsProvider key={customerId} customerId={customerId}>
      <CustomerProductApprovalList />
    </CustomerProductApprovalsProvider>
  );
}

function CustomerProductApprovalList() {
  const { t } = useTranslation();
  const {
    value: selectedProductApproval,
    isOpen,
    onOpen,
    onClose,
  } = useValueDisclosure<ProductApproval>();

  const {
    productApprovals,
    error,
    loading,
    customerFiltersError,
    searchQuery,
    setSearchQuery,
    triggerRefetchAll,
  } = useCustomerProductApprovals();

  const onStateChange = useCallback((id: number) => {
    productApprovalEvents.productApprovalChanged.dispatch({ id });
  }, []);

  const { colorMode } = useColorMode();

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const rowIds = useMemo(
    () => [...new Set(Object.keys(selectedRows).map((x) => parseInt(x)))],
    [selectedRows],
  );

  const columns = useMemo(() => {
    if (productApprovals)
      return customerProductApprovalColumns(colorMode === 'dark', t, (id) => {
        if (id) onStateChange(id);
        else triggerRefetchAll();
      });
  }, [productApprovals, colorMode, t, triggerRefetchAll, onStateChange]);

  return (
    <BodyOverride>
      <Flex flexDirection="column" gap={2}>
        <Flex flexDir="column" justifyContent="space-between">
          <TexasHeader text={t('productApproval.productApprovals')} />
          <ProductApprovalMenu
            selectedIds={rowIds}
            allIds={productApprovals?.allIds ?? []}
          />
        </Flex>
        <CustomerProductApprovalFilters />
        <Flex gap={2}>
          <Flex
            flexGrow={1}
            borderRadius={8}
            backgroundColor="texas.bg.900"
            _light={{ backgroundColor: 'white' }}
          >
            <Box width="100%" minH={0} padding={2} minW={0}>
              <DataTableContainer
                w="100%"
                error={error ?? customerFiltersError}
                datatable={{
                  selectionProps: {
                    setSelectedRows: setSelectedRows,
                    selectedRows: selectedRows,
                  },
                  data: productApprovals?.items ?? [],
                  sorted: {
                    onSortedChange: ({ key, desc }) => {
                      setSearchQuery({
                        ...searchQuery,
                        sortBy: key,
                        sortDesc: desc,
                        page: 1,
                      });
                    },
                    key: searchQuery.sortBy,
                    desc: searchQuery.sortDesc,
                  },
                  isLoading: loading,
                  columns: columns ?? [],
                  rowProps: {
                    uniqueRowStyle: (originalRow) => {
                      if (originalRow.mainSupplier) {
                        return {
                          fontStyle: 'italic',
                        };
                      }
                      return null;
                    },
                    onRowClick: (id) => {
                      const approval = productApprovals?.items.find(
                        (item) => item.id === id,
                      );
                      if (approval) onOpen(approval);
                    },
                    getRowId: (originalRow, _) => {
                      return `${originalRow.id}-${originalRow.branchId}-${originalRow.supplierId}`;
                    },
                  },
                  variant: 'configuration',
                }}
                pagination={{
                  totalItems: productApprovals?.totalItems ?? 0,
                  pageSize: defaultPageSize,
                  currentPage: searchQuery.page,
                  onPageChange: (page) =>
                    setSearchQuery({
                      ...searchQuery,
                      page,
                    }),
                }}
              />
            </Box>
          </Flex>
        </Flex>
        {selectedProductApproval && (
          <CustomerProductApprovalModal
            productApproval={selectedProductApproval}
            isOpen={isOpen}
            onClose={onClose}
            refetchAll={triggerRefetchAll}
          />
        )}
      </Flex>
    </BodyOverride>
  );
}
